import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, MapPin, ArrowLeft, Play, Pause, Square, Delete } from "lucide-react";
import api from "@/lib/api";

export default function ClockTerminal() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [sel, setSel] = useState(null);
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState("");
  const [clock, setClock] = useState(new Date());
  const off = useRef(0);

  const load = () => api.get(`/terminal/${token}`).then((r) => { setInfo(r.data); off.current = new Date(r.data.server_time).getTime() - Date.now(); }).catch((e) => setErr(e.response?.data?.detail || "Error"));
  useEffect(() => { load(); }, [token]);
  useEffect(() => { const i = setInterval(() => setClock(new Date(Date.now() + off.current)), 1000); return () => clearInterval(i); }, []);

  const act = async (action) => {
    if (pin.length < 4) { toast.error("PIN eingeben"); return; }
    setBusy(action);
    try {
      const r = await api.post(`/terminal/clock`, { token, worker_id: sel.worker_id, pin, action });
      const lbl = { CLOCK_IN: "Arbeitsstart erfasst", BREAK_START: "Pause gestartet", BREAK_END: "Pause beendet", CLOCK_OUT: "Arbeitsende erfasst" }[action];
      toast.success(`${sel.name}: ${lbl}`);
      if (r.data.mismatch) toast.warning("Achtung: nicht zugewiesene Baustelle");
      setSel(null); setPin("");
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); } finally { setBusy(""); }
  };

  if (err) return <Wrap><p className="text-slate-400" data-testid="terminal-error">{err}</p></Wrap>;
  if (!info) return <Wrap><Loader2 className="h-6 w-6 animate-spin text-cyan" /></Wrap>;

  return (
    <div className="min-h-screen bg-navy px-5 py-6 text-white" data-testid="terminal-screen">
      <div className="mx-auto max-w-2xl">
        <div className="mb-4 flex items-center justify-between">
          <div><span className="font-heading text-xl font-extrabold">Employ<span className="text-cyan">Guard</span></span><div className="flex items-center gap-1 text-sm text-cyan"><MapPin className="h-4 w-4" />{info.site?.name}</div></div>
          <div className="text-right"><div className="font-heading text-3xl font-extrabold tabular-nums">{clock.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Berlin" })}</div><div className="text-xs text-slate-400">Terminal</div></div>
        </div>

        {!sel ? (
          <>
            <p className="mb-3 text-sm text-slate-400">Mitarbeiter wählen</p>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="terminal-worker-grid">
              {info.workers.filter((w) => w.has_pin).map((w) => (
                <button key={w.worker_id} onClick={() => setSel(w)} className="rounded-2xl bg-white/5 p-4 text-left hover:bg-white/10" data-testid={`terminal-worker-${w.worker_id}`}>
                  <div className="font-semibold">{w.name}</div>
                  <div className="text-[11px] text-slate-400">{w.worker_id}</div>
                  <span className={`mt-1 inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold ${w.type === "INTERNAL" ? "bg-cyan/20 text-cyan" : "bg-white/10 text-slate-300"}`}>{w.type === "INTERNAL" ? "INTERN" : "SUB"}</span>
                </button>
              ))}
            </div>
            {info.workers.filter((w) => w.has_pin).length === 0 && <p className="mt-6 text-center text-sm text-slate-500">Keine Mitarbeiter mit PIN. Bitte im Dashboard PIN vergeben.</p>}
          </>
        ) : (
          <div>
            <button onClick={() => { setSel(null); setPin(""); }} className="mb-3 flex items-center gap-1 text-sm text-slate-400"><ArrowLeft className="h-4 w-4" /> Zurück</button>
            <div className="mb-3 rounded-2xl bg-white/5 p-4"><div className="font-heading text-lg font-extrabold">{sel.name}</div><div className="text-xs text-slate-400">{sel.worker_id}</div></div>
            <div className="mb-3 text-center font-heading text-3xl tracking-[0.3em]" data-testid="terminal-pin-display">{pin.replace(/./g, "•") || "____"}</div>
            <div className="mx-auto grid max-w-xs grid-cols-3 gap-2">
              {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((n) => <button key={n} onClick={() => setPin((p) => (p + n).slice(0, 6))} className="rounded-xl bg-white/10 py-4 text-xl font-bold hover:bg-white/20" data-testid={`pin-${n}`}>{n}</button>)}
              <button onClick={() => setPin("")} className="rounded-xl bg-white/5 py-4 text-sm">C</button>
              <button onClick={() => setPin((p) => (p + "0").slice(0, 6))} className="rounded-xl bg-white/10 py-4 text-xl font-bold hover:bg-white/20" data-testid="pin-0">0</button>
              <button onClick={() => setPin((p) => p.slice(0, -1))} className="rounded-xl bg-white/5 py-4"><Delete className="mx-auto h-5 w-5" /></button>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2">
              <button onClick={() => act("CLOCK_IN")} disabled={busy} className="col-span-2 rounded-2xl bg-emerald-500 py-4 text-lg font-extrabold hover:bg-emerald-600 disabled:opacity-60" data-testid="terminal-clock-in"><Play className="mr-1 inline h-5 w-5" />ARBEITSSTART</button>
              <button onClick={() => act("BREAK_START")} disabled={busy} className="rounded-2xl bg-amber-500 py-3 font-bold hover:bg-amber-600 disabled:opacity-60"><Pause className="mr-1 inline h-4 w-4" />PAUSE</button>
              <button onClick={() => act("BREAK_END")} disabled={busy} className="rounded-2xl bg-cyan py-3 font-bold text-navy hover:bg-cyan-dark disabled:opacity-60"><Play className="mr-1 inline h-4 w-4" />PAUSE ENDE</button>
              <button onClick={() => act("CLOCK_OUT")} disabled={busy} className="col-span-2 rounded-2xl border border-white/20 py-3 font-bold hover:bg-white/10 disabled:opacity-60" data-testid="terminal-clock-out"><Square className="mr-1 inline h-4 w-4" />ARBEITSENDE</button>
            </div>
          </div>
        )}
        <div className="mt-8 text-center text-xs text-slate-500">employguard.de</div>
      </div>
    </div>
  );
}

function Wrap({ children }) { return <div className="flex min-h-screen items-center justify-center bg-navy px-6">{children}</div>; }
